import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Modal } from '@shared/ui/Modal';
import { Field, Input, Label } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryBar,
  RegistryEmpty,
  RegistryExportButton,
  RegistryFilterSelect,
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
  RegistrySearchBox,
  RegistryThSortable,
  RegistryToolbar,
  paginationWindow,
} from '@shared/ui/RegistryShell';
import {
  apiErrorText,
  useListCounterpartiesQuery,
  useCreateCounterpartyMutation,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { downloadCsv } from '@shared/lib/downloadCsv';
import { useAppSelector } from '@app/hooks';
import { counterpartyStatusLabel, counterpartyStatusTone } from '@shared/lib/statusLabels';
import { useAgentPermission } from '@entities/user';

const Note = styled.div`
  background: ${({ theme }) => theme.colors.primarySoft};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  line-height: 1.5;
`;

const Row = styled.tr`
  cursor: pointer;
  transition: background-color 120ms ease;
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const Mono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
`;

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'ACTIVE', label: 'Действующий' },
  { value: 'LIQUIDATING', label: 'Ликвидируется' },
  { value: 'LIQUIDATED', label: 'Ликвидирован' },
  { value: 'REORGANIZING', label: 'Реорганизуется' },
  { value: 'BANKRUPT', label: 'Банкрот' },
  { value: 'UNKNOWN', label: 'Не проверялся' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Все типы' },
  { value: 'LEGAL', label: 'Юр.лицо' },
  { value: 'INDIVIDUAL', label: 'ИП' },
];

const SORT_OPTIONS = [
  { value: 'updated_at', label: 'По дате обновления' },
  { value: 'created_at', label: 'По дате добавления' },
  { value: 'full_name', label: 'По названию' },
  { value: 'inn', label: 'По ИНН' },
  { value: 'status', label: 'По статусу' },
];

export function Counterparties() {
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('statut');
  // Дебаунс 300мс — не дёргаем БД на каждую букву (ILIKE по 3 полям).
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sort, setSort] = useState('updated_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  // При смене любого фильтра — сбрасываем пагинацию на первую страницу.
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, typeFilter, sort, order]);

  const queryArg = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      party_type: typeFilter || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, statusFilter, typeFilter, sort, order, page],
  );

  const { data, isLoading, isFetching } = useListCounterpartiesQuery(queryArg);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const egrulEnabled = data?.egrul_enabled ?? false;

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [isExporting, setIsExporting] = useState(false);
  async function onExport() {
    setIsExporting(true);
    try {
      await downloadCsv(
        '/agents/statut/counterparties/export.csv',
        { search, status: statusFilter, party_type: typeFilter, sort, order },
        accessToken,
        'counterparties.csv',
      );
    } finally {
      setIsExporting(false);
    }
  }

  const [createOpen, setCreateOpen] = useState(false);
  const [inn, setInn] = useState('');
  const [name, setName] = useState('');
  const [createCounterparty, { isLoading: isCreating }] = useCreateCounterpartyMutation();
  const [createError, setCreateError] = useState<string | null>(null);

  function openCounterparty(cpId: string) {
    navigate(`/agents/statut/counterparties/${cpId}`);
  }

  function toggleSort(field: string) {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('desc');
    }
  }

  function sortArrow(field: string): string {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  }

  const pages = paginationWindow(page, totalPages);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    try {
      const c = await createCounterparty({
        inn: inn.trim(),
        full_name: name.trim() || undefined,
        auto_lookup: true,
      }).unwrap();
      setCreateOpen(false);
      setInn('');
      setName('');
      openCounterparty(c.id);
    } catch (e) {
      setCreateError(apiErrorText(e, 'Не удалось создать запись'));
    }
  }

  return (
    <>
      <Note>
        Реестр контрагентов по ИНН. Для новых записей данные подтягиваются из ЕГРЮЛ (через Dadata) —
        название, адрес, руководитель, статус компании. Договоры и претензии привязываются к
        контрагенту, чтобы видеть всю историю работы по одному юр.лицу в одном месте.
        {!egrulEnabled && (
          <>
            <br />
            <strong>Внимание:</strong> проверка ЕГРЮЛ не настроена (нет
            <code> DADATA_API_KEY </code>в окружении backend). Можно создавать записи вручную, но
            автоподтягивание данных недоступно.
          </>
        )}
      </Note>

      <RegistryToolbar>
        <RegistrySearchBox
          placeholder="Поиск по ИНН или названию…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <RegistryFilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          title="Фильтр по статусу"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          title="Фильтр по типу"
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          title="Сортировка"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <Button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          + Новый контрагент
        </Button>
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${total}${isFetching ? ' · обновление…' : ''}`}
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 12,
            color: 'var(--osnova-text-muted, #888)',
          }}
        >
          {search && (
            <span>
              По запросу «<strong>{search}</strong>»{' · '}
              <button
                type="button"
                onClick={() => setSearchInput('')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                сбросить
              </button>
            </span>
          )}
          <RegistryExportButton
            type="button"
            onClick={onExport}
            disabled={isExporting || total === 0}
            $disabled={isExporting || total === 0}
            title="Скачать текущую выборку в CSV для Excel"
          >
            {isExporting ? 'Готовим…' : 'Экспорт CSV'}
          </RegistryExportButton>
        </span>
      </RegistryBar>

      <Card>
        <Table>
          <thead>
            <tr>
              <RegistryThSortable $active={sort === 'inn'} onClick={() => toggleSort('inn')}>
                ИНН{sortArrow('inn')}
              </RegistryThSortable>
              <RegistryThSortable
                $active={sort === 'full_name'}
                onClick={() => toggleSort('full_name')}
              >
                Название{sortArrow('full_name')}
              </RegistryThSortable>
              <th>ОПФ</th>
              <RegistryThSortable $active={sort === 'status'} onClick={() => toggleSort('status')}>
                Статус{sortArrow('status')}
              </RegistryThSortable>
              <th>Адрес</th>
              <RegistryThSortable
                $active={sort === 'updated_at'}
                onClick={() => toggleSort('updated_at')}
              >
                Последняя проверка{sortArrow('updated_at')}
              </RegistryThSortable>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <Skeleton $width={90} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width="80%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={50} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width="70%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={80} $height={14} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <RegistryEmpty colSpan={6}>
                  {search || statusFilter || typeFilter
                    ? 'По заданным фильтрам ничего не найдено. Попробуйте сбросить условия.'
                    : 'Реестр пуст. Добавьте первого контрагента — ЕГРЮЛ заполнит данные автоматически.'}
                </RegistryEmpty>
              </tr>
            ) : (
              items.map((c) => (
                <Row key={c.id} onClick={() => openCounterparty(c.id)}>
                  <td>
                    <Mono>{c.inn}</Mono>
                  </td>
                  <td>{c.short_name ?? c.full_name}</td>
                  <td>{c.legal_form ?? '—'}</td>
                  <td>
                    <Badge $tone={counterpartyStatusTone(c.status)}>
                      {counterpartyStatusLabel(c.status)}
                    </Badge>
                  </td>
                  <td
                    style={{
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={c.address ?? ''}
                  >
                    {c.address ?? '—'}
                  </td>
                  <td>
                    {c.last_checked_at
                      ? new Date(c.last_checked_at).toLocaleDateString('ru-RU')
                      : '—'}
                  </td>
                </Row>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {total > PAGE_SIZE && (
        <RegistryPagination>
          <span>
            Стр. {page + 1} из {totalPages} · показано {items.length} из {total}
          </span>
          <RegistryPageButtons>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Назад
            </RegistryPageBtn>
            {pages.map((p, idx) => {
              const prev = idx > 0 ? pages[idx - 1] : -1;
              const showGap = idx > 0 && p - prev > 1;
              return (
                <Fragment key={p}>
                  {showGap && <RegistryPageBtn disabled>…</RegistryPageBtn>}
                  <RegistryPageBtn type="button" $active={p === page} onClick={() => setPage(p)}>
                    {p + 1}
                  </RegistryPageBtn>
                </Fragment>
              );
            })}
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Вперёд →
            </RegistryPageBtn>
          </RegistryPageButtons>
        </RegistryPagination>
      )}

      <Modal
        open={createOpen}
        title="Новый контрагент"
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
      >
        <form onSubmit={onCreate}>
          <Field>
            <Label htmlFor="cp-inn">ИНН *</Label>
            <Input
              id="cp-inn"
              value={inn}
              onChange={(e) => setInn(e.target.value.replace(/\D/g, '').slice(0, 12))}
              placeholder="10 цифр для юр.лица, 12 для ИП"
              inputMode="numeric"
              autoFocus
              required
            />
            {egrulEnabled && (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--osnova-text-muted, #888)',
                  marginTop: 4,
                }}
              >
                После сохранения данные автоматически подтянутся из ЕГРЮЛ.
              </span>
            )}
          </Field>
          <Field>
            <Label htmlFor="cp-name">
              Название {egrulEnabled ? '(если ЕГРЮЛ не найдёт)' : '*'}
            </Label>
            <Input
              id="cp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='напр., ООО "Стройком"'
              required={!egrulEnabled}
            />
          </Field>
          {createError && (
            <div
              style={{
                color: 'var(--osnova-danger, #d04848)',
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {createError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              type="submit"
              disabled={isCreating || inn.length < 10 || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isCreating ? 'Сохраняем…' : 'Создать'}
            </Button>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => {
                setCreateOpen(false);
                setCreateError(null);
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      </Modal>

    </>
  );
}
